/**
 * The client's review of one month (Pen chapter 08, 5 Client review: r9LhBH,
 * Nttnv, m2w7Id, z2Oir, MHs3c, E4579, M5opM; tablet rKnVK and tVa0C; phone
 * YraR4, XlAvc, Li4nD).
 *
 * Desk: the list on the left (360), the open post, and the comments rail on
 * the right; while the client comments the list folds to its dates. Tablet:
 * the dates column, the post, and the comments as a sheet from the right.
 * Phone: the list is home; a post opens full screen with "Comments n" and
 * Approve floating at the foot, the comments in a bottom sheet, and a tap on
 * a part opens the composer on it.
 *
 * Keys (the ? dialog): C comment on the part in focus, Tab the next part, J
 * and K the next and previous comment, R reply, E mark done, Esc close; 1 and
 * 2 Concept or Creative brief, [ and ] the previous or next post, V the
 * versions; Cmd or Ctrl Enter approve (after a confirm).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, MessageSquare } from "lucide-react";
import { queue as toastQueue } from "@/components/Toast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/utils/helpers";
import type { PortalReview } from "@/types/clientPortal.type";
import { CoShellPortalContext } from "@/app/routes/dashboard/CoShellPortalContext";
import { EmptyState } from "@/app/routes/dashboard/components/ViewStates";
import type { PostViewport } from "@/app/routes/dashboard/content-calendar/components/StatusBanner";
import "@/app/routes/dashboard/content-calendar/components/postPage.css";
import { pillarHue } from "@/app/routes/dashboard/content-calendar/month/calendarModel";
import { monthLabel, MONTH_NAMES } from "@/app/routes/dashboard/content-calendar/calendar.utils";
import { useSubmitDecision } from "../api/useClientPortal";
import { DECLINE_REASON_MIN_LENGTH, useSubmitBriefDecision, type ClientBriefReview } from "../api/useClientBriefs";
import type { PortalSource } from "../api/clientPortalApi";
import {
  AGENCY,
  activeConceptVersion,
  answerOf,
  briefVisible,
  clientBanner,
  clientCommentsClosed,
  clientThreads,
  conceptApproved,
  groupPosts,
  homeSentence,
  listOrder,
  railSubtitle,
  roundOf,
  type ClientAnswer,
  type ClientFuture,
  type ClientPost,
  type ClientRound,
} from "../clientReviewModel";
import { ClientDateColumn, ClientPostList } from "./ClientPostList";
import ClientPostView from "./ClientPostView";
import {
  CLIENT_COMPOSER_ID,
  ClientCommentsRail,
  ClientCommentsSheet,
  ClientCommentsTab,
  ClientPhoneComposer,
  type ClientCommentsProps,
} from "./ClientComments";
import { markersOf, useClientComments, type ClientCommentPart } from "./useClientComments";
import { ApproveConfirmDialog, AskForChangesDialog, ShortcutsDialog } from "./ClientDialogs";
import "./clientReview.css";

/** A key press meant for a field, a menu or a dialog, not for the page. */
/** Where the page opens: the design harness's states. */
export interface ClientReviewInitial {
  postId?: string;
  tab?: ClientRound;
  commenting?: boolean;
  shortcuts?: boolean;
  /** The comments sheet on a tablet or phone. */
  sheet?: boolean;
  /** The part being commented on, as a hover or C would set it. */
  anchor?: ClientCommentPart;
}

function isTyping(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  if (target.closest("input, textarea, select, [contenteditable='true'], [role='menu'], [role='listbox'], [role='dialog']")) return true;
  return false;
}

function focusComposer() {
  // Two frames: one for the rail or sheet to mount, one for its field.
  requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById(CLIENT_COMPOSER_ID)?.focus()));
}

function errorMessage(error: unknown): string {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return message ?? "Your answer did not go through. Please try again.";
}

export default function ClientReviewPage({
  source,
  posts,
  brandName,
  year,
  month,
  readerName,
  review,
  briefReview,
  readOnly = false,
  embedded = false,
  fill = false,
  toolbar,
  onSelectPost,
  theme = "light",
  future = {},
  onSwitchReader,
  now: fixedNow,
  initial,
}: {
  source: PortalSource;
  posts: ClientPost[];
  brandName: string;
  year: number;
  month: number;
  readerName: string;
  review: PortalReview | null;
  briefReview: ClientBriefReview | null;
  /** A guest who may read the month but not answer it. */
  readOnly?: boolean;
  /** Inside the app for a signed-in guest: no shell of its own. */
  embedded?: boolean;
  /** Embedded and filling its parent's height, with no frame (the team's client preview). */
  fill?: boolean;
  toolbar?: ReactNode;
  /** The post on screen, as the reader picks one (null on the phone's list). */
  onSelectPost?: (slotId: string | null) => void;
  /** A standalone shell's theme (the design harness); a guest's page follows the cockpit. */
  theme?: "light" | "dark";
  /** Backend fields not sent yet (#129, #130); absent keeps them hidden. */
  future?: ClientFuture;
  /** "Not you?" under the list: sign out. Absent hides it. */
  onSwitchReader?: () => void;
  /** The design harness's clock. */
  now?: number;
  /** The design harness's opening view. */
  initial?: ClientReviewInitial;
}) {
  const desk = useMediaQuery("(min-width: 1024px)");
  const wide = useMediaQuery("(min-width: 768px)");
  const viewport: PostViewport = desk ? "desktop" : wide ? "tablet" : "phone";
  const phone = viewport === "phone";
  const [now] = useState(() => fixedNow ?? Date.now());
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  const groups = useMemo(() => groupPosts(posts), [posts]);
  const order = useMemo(() => listOrder(posts), [posts]);
  const counts = useMemo(
    () => Object.fromEntries(posts.map((post) => [post.slot.id, clientThreads(post).length])),
    [posts],
  );
  // The pillar rule's colour: the month's pillars in the order they first appear.
  const pillarHues = useMemo(() => {
    const names: string[] = [];
    for (const post of [...posts].sort((a, b) => a.slot.scheduledAt.localeCompare(b.slot.scheduledAt))) {
      const name = post.slot.pillarName?.trim();
      if (name && !names.includes(name)) names.push(name);
    }
    return new Map(names.map((name, index) => [name, pillarHue(index)]));
  }, [posts]);

  const [pickedId, setPickedId] = useState<string | null>(initial?.postId ?? null);
  const selected =
    posts.find((post) => post.slot.id === pickedId) ?? (phone ? null : (order[0] ?? null));
  const selectedId = selected?.slot.id ?? null;
  // Beside the list the first post opens by itself; pin it, so an answer that
  // moves it to another group does not swap the post under the reader.
  useEffect(() => {
    if (!phone && pickedId === null && selectedId) setPickedId(selectedId);
  }, [phone, pickedId, selectedId]);
  useEffect(() => {
    onSelectPost?.(selectedId);
  }, [onSelectPost, selectedId]);

  const [tabs, setTabs] = useState<Record<string, ClientRound>>(() =>
    initial?.postId && initial.tab ? { [initial.postId]: initial.tab } : {},
  );
  const tab: ClientRound = selected
    ? (tabs[selected.slot.id] ?? roundOf(selected)) === "brief" && briefVisible(selected)
      ? "brief"
      : "concept"
    : "concept";
  const [viewing, setViewing] = useState<Record<string, number | null>>({});
  const viewingKey = selected ? `${selected.slot.id}:${tab}` : "";
  const viewingVersion = viewing[viewingKey] ?? null;

  const [commenting, setCommenting] = useState(!!initial?.commenting);
  const [listFolded, setListFolded] = useState(!!initial?.commenting);
  const [tabletListOpen, setTabletListOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(!!initial?.sheet);
  const [askOpen, setAskOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(!!initial?.shortcuts);

  const submitConcept = useSubmitDecision(source);
  const submitBrief = useSubmitBriefDecision(source);

  // ------------------------------------------------------------ the banner
  // One answer per post: on either tab it decides the idea and its brief
  // together, except a brief still in the old loop, which is answered alone.
  const round: ClientAnswer = selected ? answerOf(selected, tab) : "concept";
  const rawBanner = selected
    ? clientBanner(selected, tab, {
        viewingVersion,
        sentAt: round === "brief" ? (briefReview?.sentAt ?? null) : null,
        readerName,
        future,
      })
    : null;
  const banner = rawBanner && readOnly ? { ...rawBanner, primary: undefined, canAskForChanges: false } : rawBanner;

  const active = selected ? activeConceptVersion(selected.slot) : null;

  const deciding = submitConcept.isPending || submitBrief.isPending;

  const approve = () => {
    // One answer at a time: a second press while the first is out does nothing.
    if (!selected || !banner?.primary || deciding) return;
    setConfirmOpen(false);
    const onError = (error: unknown) =>
      toastQueue.add({ title: "Not approved", description: errorMessage(error), variant: "error" });
    // Told at once, as the answer goes out; a refusal rolls it back with its
    // own toast. Undo needs a client un-approve route that does not exist, so
    // the toast has none.
    const version = round === "brief" ? (selected.brief?.version ?? 1) : (active?.version ?? 1);
    toastQueue.add({
      title: `You approved version ${version}`,
      description: `${round === "post" ? "The idea and its creative brief" : round === "brief" ? "Creative brief" : "Concept"}. ${AGENCY} has been told.`,
      variant: "success",
    });
    if (round === "brief") {
      submitBrief.mutate({ slotId: selected.slot.id, decision: "APPROVED" }, { onError });
    } else {
      submitConcept.mutate(
        { slotId: selected.slot.id, conceptVersionId: active?.id, decision: "APPROVED" },
        { onError },
      );
    }
  };

  const askForChanges = (reason: string) => {
    if (!selected || deciding) return;
    setAskOpen(false);
    const onError = (error: unknown) =>
      toastQueue.add({ title: "Not sent", description: errorMessage(error), variant: "error" });
    toastQueue.add({ title: `Sent to ${AGENCY}`, description: "They will send you a new version.", variant: "success" });
    if (round === "brief") {
      submitBrief.mutate({ slotId: selected.slot.id, decision: "DECLINED", reason }, { onError });
    } else {
      submitConcept.mutate(
        { slotId: selected.slot.id, conceptVersionId: active?.id, decision: "DECLINED", reason },
        { onError },
      );
    }
  };

  // ------------------------------------------------------------ comments
  const brief = selected?.brief ?? null;
  // No feedback after approval: the idea and the brief approved closes comments.
  const closedLine = selected ? clientCommentsClosed(selected, brandName) : null;
  const conceptTarget =
    !readOnly && !closedLine && selected && selected.slot.actionable && active && (tab !== "concept" || !viewingVersion || viewingVersion === active.version)
      ? { id: active.id, version: active.version }
      : null;
  const briefTarget =
    !readOnly && !closedLine && selected && brief && briefVisible(selected) && briefReview?.commentsOpen && (tab !== "brief" || !viewingVersion || viewingVersion === brief.version)
      ? { id: brief.briefId, version: brief.version }
      : null;
  const comments = useClientComments({
    post: selected,
    source,
    authorName: readerName,
    targets: { concept: conceptTarget, brief: briefTarget },
  });
  const oldVersion = viewingVersion !== null;
  const disabledReason =
    tab === "concept"
      ? conceptTarget
        ? null
        : oldVersion
          ? "Older versions are read only."
          : selected && conceptApproved(selected.slot)
            ? "You approved the idea, so its comments are closed."
            : "Comments on the idea are closed right now."
      : briefTarget
        ? null
        : oldVersion
          ? "Older versions are read only."
          : "Comments on the creative brief are closed right now.";
  const shownVersion =
    viewingVersion ?? (tab === "brief" ? (brief?.version ?? 1) : (active?.version ?? 1));
  // The part a comment is on ("the hook"): sent with the comment as its anchor.
  const [anchor, setAnchor] = useState<{ postId: string; part: ClientCommentPart } | null>(() =>
    initial?.anchor && initial.postId ? { postId: initial.postId, part: initial.anchor } : null,
  );
  const hoveredPart = useRef<ClientCommentPart | null>(null);
  const canCommentHere = !readOnly && !closedLine && !!(tab === "concept" ? conceptTarget : briefTarget);
  const anchorHere = anchor && anchor.postId === selectedId && canCommentHere ? anchor.part : null;
  // The selected thread: open in the rail, its marker lit on the page.
  const [openThread, setOpenThread] = useState<{ postId: string; id: string } | null>(null);
  const openId = openThread && openThread.postId === selectedId ? openThread.id : null;
  const [reveal, setReveal] = useState(0);
  const markers = useMemo(() => markersOf(comments.threads, tab, shownVersion), [comments.threads, tab, shownVersion]);
  const openMarker = markers.find((marker) => marker.threadId === openId) ?? null;
  const activePart = anchorHere ?? (openMarker ? { part: openMarker.part, key: openMarker.key } : null);
  // A new comment on a part takes the next number on this version.
  const nextNumber = markers.reduce((max, marker) => Math.max(max, marker.number), 0) + 1;
  const showOnPage = (id: string) => {
    if (!selectedId) return;
    setAnchor(null);
    setOpenThread({ postId: selectedId, id });
    setReveal((value) => value + 1);
    if (viewport !== "desktop") setSheetOpen(false);
  };
  const commentsProps: ClientCommentsProps = {
    data: comments,
    brandName,
    readerName,
    part: tab,
    subtitle: closedLine ? closedLine.replace(/^Comments are closed\. /, "Comments are closed: ") : railSubtitle(shownVersion),
    disabledReason,
    closedLine,
    anchor: anchorHere ? { label: anchorHere.label, onClear: () => setAnchor(null) } : null,
    anchorPart: anchorHere,
    readOnly,
    postKey: selectedId ?? "none",
    openId,
    onOpenThread: (id) => setOpenThread(id && selectedId ? { postId: selectedId, id } : null),
    onShowOnPage: showOnPage,
  };
  const [phoneComposer, setPhoneComposer] = useState(!!initial?.anchor && phone);

  const openComments = (focus = false, part: ClientCommentPart | null = null) => {
    if (part && selectedId && canCommentHere) {
      setAnchor({ postId: selectedId, part });
      setOpenThread(null);
    }
    // A phone comments on a part in its own sheet, on the keyboard.
    if (viewport === "phone" && part && canCommentHere) {
      setPhoneComposer(true);
      return;
    }
    if (viewport === "desktop") {
      setCommenting(true);
      setListFolded(true);
    } else {
      setSheetOpen(true);
    }
    if (focus && !readOnly) focusComposer();
  };
  const closeComments = () => {
    setCommenting(false);
    setListFolded(false);
  };

  const select = (slotId: string) => {
    setPickedId(slotId);
    setTabletListOpen(false);
  };

  // A new post starts at its top, with nothing hovered.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    hoveredPart.current = null;
  }, [selectedId]);

  // ------------------------------------------------------------ keys
  const live = { order, selectedId, banner, viewport, commenting, phone, canCommentHere, comments, openId, post: selected };
  const keyState = useRef(live);
  keyState.current = live;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey) return;
      if (askOpen || confirmOpen || shortcutsOpen) return;
      const state = keyState.current;
      // Cmd or Ctrl Enter approves, after a confirm, from anywhere but a field.
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        if (isTyping(event) || !state.banner?.primary) return;
        event.preventDefault();
        setConfirmOpen(true);
        return;
      }
      if (event.metaKey || event.ctrlKey) return;
      if (event.key === "Escape") {
        if (isTyping(event)) return;
        if (state.commenting) closeComments();
        else if (state.phone && state.selectedId) setPickedId(null);
        return;
      }
      if (isTyping(event)) return;
      const key = event.key === "?" ? "?" : event.key.toLowerCase();
      const threads = [...state.comments.open, ...state.comments.done];
      const threadIndex = threads.findIndex((thread) => thread.id === state.openId);
      const selectThread = (id: string) => {
        if (!state.selectedId) return;
        setOpenThread({ postId: state.selectedId, id });
        if (state.viewport === "desktop") {
          setCommenting(true);
          setListFolded(true);
        } else {
          setSheetOpen(true);
        }
        requestAnimationFrame(() =>
          document.getElementById(`co-client-thread-${id}`)?.scrollIntoView({ block: "nearest" }),
        );
      };
      if (key === "?") {
        event.preventDefault();
        setShortcutsOpen(true);
      } else if (key === "[" || key === "]") {
        if (!state.order.length) return;
        event.preventDefault();
        const index = state.order.findIndex((post) => post.slot.id === state.selectedId);
        const next = key === "]" ? Math.min(state.order.length - 1, index + 1) : Math.max(0, index - 1);
        const target = state.order[index < 0 ? 0 : next];
        if (target) select(target.slot.id);
      } else if (key === "j" || key === "k") {
        if (!threads.length) return;
        event.preventDefault();
        const next =
          threadIndex < 0 ? 0 : key === "j" ? Math.min(threads.length - 1, threadIndex + 1) : Math.max(0, threadIndex - 1);
        selectThread(threads[next].id);
      } else if (key === "r") {
        const thread = threads[threadIndex];
        if (!thread || readOnly || !state.comments.canReply(thread)) return;
        event.preventDefault();
        selectThread(thread.id);
        requestAnimationFrame(() =>
          requestAnimationFrame(() =>
            document
              .getElementById(`co-client-thread-${thread.id}`)
              ?.querySelector<HTMLTextAreaElement>("textarea")
              ?.focus(),
          ),
        );
      } else if (key === "e") {
        const thread = threads[threadIndex];
        if (!thread || readOnly || thread.id.startsWith("local:")) return;
        event.preventDefault();
        state.comments.toggleDone(thread.id);
      } else if (key === "1" || key === "2") {
        const post = state.post;
        if (!post) return;
        const next: ClientRound = key === "1" ? "concept" : "brief";
        if (next === "brief" && !briefVisible(post)) return;
        event.preventDefault();
        setTabs((all) => ({ ...all, [post.slot.id]: next }));
      } else if (key === "v") {
        const buttons = mainRef.current?.querySelectorAll<HTMLButtonElement>("article button") ?? [];
        const trigger = [...buttons].find((button) => /^Version \d+/.test(button.textContent?.trim() ?? ""));
        if (!trigger) return;
        event.preventDefault();
        trigger.click();
      } else if (key === "c") {
        if (!state.selectedId) return;
        event.preventDefault();
        openComments(true, state.canCommentHere ? hoveredPart.current : null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // The handler reads the live state through keyState; it rebinds only when a dialog opens or closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askOpen, confirmOpen, shortcutsOpen]);

  // ------------------------------------------------------------ parts
  const monthTitle = monthLabel({ year, month });
  const sentence = homeSentence(posts, now, future, readOnly);
  const listProps = {
    brandName,
    monthTitle,
    sentence,
    groups,
    selectedId,
    onSelect: select,
    readerName,
    counts,
    now,
    future,
    onSwitchReader,
  };

  const postView = selected && banner ? (
    <ClientPostView
      key={selected.slot.id}
      post={selected}
      tab={tab}
      onTab={(next) => setTabs((all) => ({ ...all, [selected.slot.id]: next }))}
      viewport={viewport}
      brandName={brandName}
      banner={banner}
      busy={deciding}
      viewingVersion={viewingVersion}
      onPickVersion={(version) => setViewing((all) => ({ ...all, [viewingKey]: version }))}
      onApprove={approve}
      onAskForChanges={() => setAskOpen(true)}
      onCommentPart={canCommentHere ? (part) => openComments(true, part) : null}
      onHoverPart={(part) => {
        hoveredPart.current = part;
      }}
      markers={markers}
      activePart={activePart}
      activeNumber={anchorHere ? nextNumber : null}
      onMarker={showOnPage}
      reveal={reveal}
      pillarHue={pillarHues.get(selected.slot.pillarName?.trim() ?? "") ?? null}
      future={future}
      comments={viewport === "tablet" ? { count: comments.count, onOpen: () => openComments() } : undefined}
      onBack={phone ? () => setPickedId(null) : undefined}
      monthName={MONTH_NAMES[month - 1] ?? monthTitle}
    />
  ) : null;

  const emptyPost = (
    <div className="grid h-full min-h-[420px] place-items-center">
      <EmptyState
        minHeight={320}
        icon={<MessageSquare size={20} strokeWidth={1.5} aria-hidden="true" />}
        title="Nothing to review yet"
        body={`When ${AGENCY} sends posts for your review, they show up here.`}
      />
    </div>
  );

  let body: ReactNode;
  if (phone) {
    body = selected ? (
      <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto px-4 pb-24">
        {postView}
        {/* Bar / Bottom · Phone: no tray; Comments in chrome glass and Approve floating on its own shadow. */}
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-8 pt-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => openComments()}
              className="co-glass-chrome flex h-12 min-w-28 shrink-0 items-center justify-center rounded-pill px-4 text-[15px] font-medium text-co-text outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
            >
              {comments.count > 0 ? `Comments ${comments.count}` : "Comments"}
            </button>
            {banner?.primary ? (
              <button
                type="button"
                onClick={approve}
                disabled={deciding}
                className="flex h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-pill bg-co-accent-fill text-[15px] font-semibold text-co-on-accent shadow-[0_8px_24px_var(--co-shell-shadow)] outline-none transition-[transform,background-color] hover:bg-[var(--co-accent-fill-hover)] active:scale-[0.97] disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
              >
                {banner.primary.label}
                <Check className="size-4" strokeWidth={2} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </main>
    ) : (
      <div className="min-h-0 flex-1">
        <ClientPostList {...listProps} phone />
      </div>
    );
  } else {
    const folded = viewport === "tablet" ? !tabletListOpen : listFolded;
    body = (
      <div className="flex min-h-0 flex-1">
        <div
          className={cn(
            "co-cr-list min-h-0 shrink-0 overflow-hidden",
            // The preview floats the list as an island (the nav island's look):
            // 12 in from the edges, radius 22, chrome frost. The client's own
            // page keeps the flat rail.
            fill ? "co-post-rail-island my-3 ml-3 rounded-[22px]" : "bg-co-rail shadow-[inset_-1px_0_0_var(--co-line)]",
          )}
          style={{ inlineSize: folded ? 72 : 360 }}
        >
          {folded ? (
            <ClientDateColumn
              {...listProps}
              onUnfold={() => (viewport === "tablet" ? setTabletListOpen(true) : setListFolded(false))}
            />
          ) : (
            <ClientPostList {...listProps} />
          )}
        </div>
        <main
          ref={mainRef}
          className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto pb-16", viewport === "tablet" ? "px-6" : "px-8")}
        >
          {postView ?? emptyPost}
        </main>
        {viewport === "desktop" && selected ? (
          commenting ? (
            <ClientCommentsRail {...commentsProps} onCollapse={closeComments} />
          ) : (
            <ClientCommentsTab count={comments.count} onOpen={() => openComments()} />
          )
        ) : null}
      </div>
    );
  }

  const content = (
    <>
      {body}
      {viewport !== "desktop" && selected ? (
        <ClientCommentsSheet {...commentsProps} viewport={viewport} isOpen={sheetOpen} onOpenChange={setSheetOpen} />
      ) : null}
      {phone && selected ? (
        <ClientPhoneComposer
          key={`${selected.slot.id}:${anchorHere?.part ?? ""}:${anchorHere?.key ?? ""}`}
          isOpen={phoneComposer}
          onClose={() => {
            setPhoneComposer(false);
            setAnchor(null);
          }}
          partLabel={anchorHere ? `${tab === "brief" ? "Creative brief" : "Concept"} · ${anchorHere.label}` : null}
          number={anchorHere ? nextNumber : null}
          readerName={readerName}
          disabledReason={disabledReason}
          onPost={(text) => comments.post(tab, text, anchorHere)}
        />
      ) : null}
      <AskForChangesDialog
        key={`${selectedId}:${round}`}
        round={round}
        minimum={round === "brief" ? DECLINE_REASON_MIN_LENGTH : (review?.declineReasonMinLength ?? DECLINE_REASON_MIN_LENGTH)}
        isOpen={askOpen}
        busy={deciding}
        onOpenChange={setAskOpen}
        onSend={askForChanges}
      />
      <ApproveConfirmDialog
        label={banner?.primary?.label ?? "Approve"}
        round={round}
        isOpen={confirmOpen}
        busy={deciding}
        onOpenChange={setConfirmOpen}
        onApprove={approve}
      />
      <ShortcutsDialog isOpen={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </>
  );

  if (embedded) {
    return (
      <div className={cn("flex w-full flex-col", fill ? "h-full min-h-0" : "gap-4")}>
        {toolbar}
        <div
          className={cn(
            "flex flex-col overflow-hidden bg-co-ground text-co-text",
            fill ? "min-h-0 flex-1" : "h-[max(560px,calc(100dvh-12rem))] rounded-panel border border-co-line",
          )}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <CoShellPortalContext.Provider value={portal}>
      <div
        ref={setPortal}
        className={cn("co-shell flex h-dvh flex-col overflow-hidden bg-co-ground text-co-text", theme === "dark" && "dark")}
      >
        {content}
      </div>
    </CoShellPortalContext.Provider>
  );
}
